import { useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Check, UserRound, X } from 'lucide-react';
import { Avatar, Badge, Button, cn, Command, Popover, PopoverContent, PopoverTrigger } from '@vwt/ui';
import { useApiQuery } from '../lib/api.js';
import { useTranslation } from '../lib/i18n.js';
import { effectiveDir, listParam, nextSort, toggleListValue } from '../lib/list-sort.js';

type SetParam = (key: string, value: string | null) => void;

/**
 * Column header that sorts the list: the first click uses the column's natural direction, the next
 * one flips it. The state lives in the URL (sort, dir) so it is shareable and saved with views.
 */
export function SortHeader({ label, sortKey, params, setParams, defaultSort = 'updatedAt', className }: { label: ReactNode; sortKey: string; params: URLSearchParams; setParams: (update: (p: URLSearchParams) => URLSearchParams) => void; defaultSort?: string; className?: string }) {
  const { t } = useTranslation();
  const current = params.get('sort') ?? defaultSort;
  const active = current === sortKey;
  const dir = effectiveDir(current, params.get('dir'));
  const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        const next = nextSort(current, params.get('dir'), sortKey, defaultSort);
        setParams((p) => {
          const updated = new URLSearchParams(p);
          if (next.sort) updated.set('sort', next.sort);
          else updated.delete('sort');
          if (next.dir) updated.set('dir', next.dir);
          else updated.delete('dir');
          return updated;
        });
      }}
      aria-label={t('table.sortBy', { column: typeof label === 'string' ? label : sortKey })}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}
      className={cn('group inline-flex items-center gap-1 rounded-sm text-left hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', active && 'text-fg', className)}
    >
      {label}
      <Icon className={cn('size-3 shrink-0', active ? 'opacity-100' : 'opacity-0 group-hover:opacity-60 group-focus-visible:opacity-60')} aria-hidden />
    </button>
  );
}

/** "123 items" with how many are loaded when the list has more pages. */
export function ResultCount({ total, loaded, className }: { total: number | undefined; loaded: number; className?: string }) {
  const { t } = useTranslation();
  if (total === undefined) return null;
  return (
    <span className={cn('text-xs whitespace-nowrap text-fg-muted tabular-nums', className)} data-testid="result-count" aria-live="polite">
      {t('table.total', { count: total })}
      {loaded < total ? <span className="text-fg-subtle"> · {t('table.showing', { count: loaded })}</span> : null}
    </span>
  );
}

/** Names for people chosen in filters (the URL only keeps their ids). */
export function usePeopleNames(ids: string[]): Map<string, string> {
  const real = [...new Set(ids.filter((id) => id !== 'me' && id !== 'none'))].sort();
  const query = useApiQuery('searchPrincipals', { query: { kinds: 'USER', ids: real.join(',') } }, { enabled: real.length > 0, staleTime: 5 * 60_000 });
  return new Map((query.data?.data ?? []).map((p) => [p.id, p.name]));
}

export interface PeopleRole {
  /** URL parameter holding comma-separated ids, me or none. */
  param: string;
  label: string;
  /** Label of the "nobody" option, when the role can be empty. */
  noneLabel?: string;
}

/**
 * Filter by people in one or more roles (assignee, owner, author…): pick the role, then toggle
 * people found by search, yourself, or nobody.
 */
export function PeopleFilter({ roles, params, setParam }: { roles: PeopleRole[]; params: URLSearchParams; setParam: SetParam }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [roleIndex, setRoleIndex] = useState(0);
  const [q, setQ] = useState('');
  const role = roles[roleIndex] ?? roles[0]!;
  const selected = listParam(params, role.param);
  const count = roles.reduce((n, r) => n + listParam(params, r.param).length, 0);
  const results = useApiQuery('searchPrincipals', { query: { q, kinds: 'USER' } }, { enabled: open });
  const names = usePeopleNames(selected);
  const toggle = (value: string) => setParam(role.param, toggleListValue(params, role.param, value));
  const chosenOthers = selected.filter((id) => id !== 'me' && id !== 'none' && !(results.data?.data ?? []).some((p) => p.id === id));
  const item = (value: string, label: string, icon: ReactNode) => (
    <Command.Item key={value} value={value} onSelect={() => toggle(value)} className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm aria-selected:bg-muted">
      {icon}
      <span className="truncate">{label}</span>
      {selected.includes(value) ? <Check className="ml-auto size-3.5 text-accent" aria-label={t('table.selected')} /> : null}
    </Command.Item>
  );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="md" variant="secondary" data-testid="people-filter">
          <UserRound /> {t('table.people')}
          {count ? <Badge tone="accent">{count}</Badge> : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0">
        {roles.length > 1 ? (
          <div className="flex gap-1 border-b border-border p-1.5" role="tablist" aria-label={t('table.peopleRole')}>
            {roles.map((r, i) => (
              <button
                key={r.param}
                type="button"
                role="tab"
                aria-selected={i === roleIndex}
                onClick={() => setRoleIndex(i)}
                className={cn('flex-1 rounded-sm px-2 py-1 text-xs font-medium', i === roleIndex ? 'bg-accent-soft text-accent-soft-fg' : 'text-fg-muted hover:bg-muted')}
              >
                {r.label}
                {listParam(params, r.param).length ? ` (${listParam(params, r.param).length})` : ''}
              </button>
            ))}
          </div>
        ) : null}
        <Command shouldFilter={false} label={role.label}>
          <Command.Input value={q} onValueChange={setQ} autoFocus placeholder={t('table.searchPeople')} className="h-9 w-full border-b border-border bg-transparent px-3 text-sm outline-none" />
          <Command.List className="max-h-72 overflow-y-auto p-1">
            {!q ? item('me', t('common.me'), <Avatar name={t('common.me')} size="sm" />) : null}
            {!q && role.noneLabel ? item('none', role.noneLabel, <span className="size-5 rounded-full border border-dashed border-border-strong" aria-hidden />) : null}
            {chosenOthers.map((id) => item(id, names.get(id) ?? '…', <Avatar name={names.get(id) ?? '?'} size="sm" />))}
            {(results.data?.data ?? []).map((p) => item(p.id, p.name, <Avatar name={p.name} size="sm" />))}
            <Command.Empty className="px-2 py-4 text-center text-xs text-fg-subtle">{t('common.noResults')}</Command.Empty>
          </Command.List>
        </Command>
        {count ? (
          <div className="border-t border-border p-1">
            <Button
              size="sm"
              variant="ghost"
              className="w-full justify-start"
              onClick={() => {
                for (const r of roles) setParam(r.param, null);
              }}
            >
              <X /> {t('table.clearPeople')}
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

export interface FilterChip {
  key: string;
  label: ReactNode;
  onRemove: () => void;
}

/** Active filters as removable chips under the toolbar. */
export function FilterChips({ chips }: { chips: FilterChip[] }) {
  const { t } = useTranslation();
  if (!chips.length) return null;
  return (
    <ul className="flex flex-wrap items-center gap-1.5 pb-3" aria-label={t('table.activeFilters')} data-testid="filter-chips">
      {chips.map((chip) => (
        <li key={chip.key} className="inline-flex h-6 items-center gap-1 rounded-full border border-border-strong bg-surface-2 pr-1 pl-2.5 text-xs text-fg">
          {chip.label}
          <button type="button" onClick={chip.onRemove} className="rounded-full p-0.5 text-fg-subtle hover:bg-muted hover:text-fg" aria-label={t('table.removeFilter')}>
            <X className="size-3" />
          </button>
        </li>
      ))}
    </ul>
  );
}

/** Chips for one comma-separated people parameter, resolving names. */
export function personChips(params: URLSearchParams, setParam: SetParam, param: string, roleLabel: string, names: Map<string, string>, meLabel: string, noneLabel: string): FilterChip[] {
  return listParam(params, param).map((id) => ({
    key: `${param}:${id}`,
    label: (
      <>
        <span className="text-fg-muted">{roleLabel}:</span> {id === 'me' ? meLabel : id === 'none' ? noneLabel : (names.get(id) ?? '…')}
      </>
    ),
    onRemove: () => setParam(param, toggleListValue(params, param, id)),
  }));
}
